import { Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  IconButton,
  Link as MuiLink,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import React from "react";
import { Link } from "react-router-dom";

import { monospaceStyle, panelShadow } from "../../../theme";
import {
  CapabilityGrain,
  CapabilityMap,
  CapabilityMapNode,
} from "../../../types/analytics";
import { EntityClickHandler } from "../../../types/entityRegistry";
import {
  AnalyticsSelection,
  exploreAddress,
  selectionOf,
} from "../analyticsState";
import { areaColourOf } from "./capabilityMapColours";
import { regionIdOf } from "./capabilityMapLayout";
import { counted, nodeName, pairReading, percent } from "./CapabilityMapParts";

// Whether the selection itself picks a capability, a condition or a mode.
// Then the map's counts, which are of the products that have the node's
// capability within the selection, cannot be had from Explore by adding the
// node's pick: that pick would widen the filter (any of the picks).
export const scopePicksCapability = (selection: AnalyticsSelection): boolean =>
  selection.capability.length > 0 ||
  selection.condition.length > 0 ||
  selection.mode.length > 0;

// The selection that opens a node's products in Explore: the scope plus the
// node's pick. Where the scope picks a capability, condition or mode, the
// node's pick replaces the scope's picks of its kind instead.
export const nodeSelection = (
  selection: AnalyticsSelection,
  node: Pick<CapabilityMapNode, "capability" | "qualifier">,
  grain: CapabilityGrain,
): AnalyticsSelection => {
  const picked: AnalyticsSelection = {
    ...selectionOf(selection),
    capability: [node.capability.id],
  };
  if (node.qualifier && grain === "condition") {
    picked.condition = [node.qualifier.id];
  }
  if (node.qualifier && grain === "mode") {
    picked.mode = [node.qualifier.id];
  }
  return picked;
};

interface PartnerPair {
  partner: CapabilityMapNode;
  support: number;
  jaccard: number;
  thin: boolean;
}

// The pairs a node is in, the most alike first
export const pairsOf = (map: CapabilityMap, id: string): PartnerPair[] => {
  const byId = new Map(map.nodes.map((node) => [node.id, node]));
  const found: PartnerPair[] = [];
  for (const pair of map.pairs) {
    const otherId = pair.a === id ? pair.b : pair.b === id ? pair.a : null;
    const partner = otherId ? byId.get(otherId) : undefined;
    if (partner) {
      found.push({
        partner,
        support: pair.support,
        jaccard: pair.jaccard,
        thin: pair.thin,
      });
    }
  }
  return found.sort((x, y) => y.jaccard - x.jaccard);
};

interface CapabilityMapPanelProps {
  map: CapabilityMap;
  node: CapabilityMapNode;
  selection: AnalyticsSelection;
  onSelect: (id: string | null) => void;
  onOpenEntity: EntityClickHandler;
}

// A line of the panel: 12px, as the captions of the designs
const CAPTION_SX = { fontSize: 12, lineHeight: 1.5 } as const;

const LINK_BUTTON_SX = {
  font: "inherit",
  textAlign: "left",
  verticalAlign: "baseline",
} as const;

// The panel of a selected capability, floating over the top right of the
// canvas: where it sits, its counts, the pairs it is in and the ways on to
// Explore and to the capability itself
const CapabilityMapPanel: React.FC<CapabilityMapPanelProps> = ({
  map,
  node,
  selection,
  onSelect,
  onOpenEntity,
}) => {
  const { chart } = useTheme().palette;
  const pairs = pairsOf(map, node.id);
  const widened = scopePicksCapability(selection);
  const href = exploreAddress(nodeSelection(selection, node, map.grain));
  return (
    <Box
      component="aside"
      aria-label="Selected capability"
      sx={{
        position: "absolute",
        top: 14,
        right: 14,
        zIndex: 5,
        width: 300,
        maxWidth: "calc(100% - 28px)",
        maxHeight: "calc(100% - 28px)",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: "6px",
        p: "14px",
        overflow: "auto",
        fontSize: "12.5px",
        border: 1,
        borderColor: "divider",
        borderRadius: (theme) => `${theme.radius.surface}px`,
        backgroundColor: "background.raised",
        boxShadow: (theme) => panelShadow(theme),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <Typography
          component="h3"
          sx={{
            flex: 1,
            minWidth: 0,
            m: 0,
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1.45,
            overflowWrap: "anywhere",
          }}
        >
          {nodeName(node)}
        </Typography>
        <IconButton
          size="small"
          aria-label="Clear selection"
          onClick={() => onSelect(null)}
          sx={{ m: "-4px -6px -4px 0" }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* Where it sits: effect area, function area, and what it is */}
      <Box sx={{ ...CAPTION_SX, color: "text.secondary" }}>
        <Box
          component="span"
          aria-hidden
          sx={{
            display: "inline-block",
            width: 8,
            height: 8,
            mr: "6px",
            borderRadius: "50%",
            backgroundColor: areaColourOf(regionIdOf(node), chart),
          }}
        />
        <span>{node.effect_area?.name ?? node.effect_area?.id ?? "Unfiled"}</span>
        {" · "}
        <span>
          {node.function_area?.name ??
            node.function_area?.id ??
            "no function area on record"}
        </span>
        {" · "}
        <span>{node.core ? "ability" : "capability"}</span>
      </Box>

      <Box sx={{ ...CAPTION_SX, ...monospaceStyle }}>
        <MuiLink component={Link} to={href} underline="hover">
          {counted(node.product_count, "product")}
        </MuiLink>
        {" · "}
        <span>{counted(node.articles, "article")}</span>
      </Box>
      <Box sx={{ ...CAPTION_SX, ...monospaceStyle }}>
        stated as a need by {counted(node.need_count, "host")}
      </Box>
      {widened && (
        <Box sx={{ ...CAPTION_SX, color: "text.secondary" }}>
          Count differs from Explore while the scope picks a capability,
          condition or mode: Explore opens with this pick in place of the
          scope's.
        </Box>
      )}

      {/* The pairs: a row each, with the products the two share and a bar of
          the share that is of the products that have either */}
      <Box
        component="section"
        aria-label="Pairs of the capability"
        sx={{ ...CAPTION_SX, color: "text.secondary", mt: 0.5 }}
      >
        {pairs.length === 0 ? (
          "No pair above the similarity floor."
        ) : (
          <>
            Comes together with
            <Box
              component="ul"
              sx={{
                m: "6px 0 0",
                p: 0,
                listStyle: "none",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              {pairs.map(({ partner, support, jaccard, thin }) => (
                <Box
                  component="li"
                  key={partner.id}
                  title={pairReading(node, partner, support, jaccard)}
                >
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                    <MuiLink
                      component="button"
                      type="button"
                      underline="hover"
                      onClick={() => onSelect(partner.id)}
                      sx={{ ...LINK_BUTTON_SX, flex: 1, minWidth: 0 }}
                    >
                      {nodeName(partner)}
                    </MuiLink>
                    <Box
                      component="span"
                      sx={{
                        ...monospaceStyle,
                        color: "text.primary",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {support} shared · {percent(jaccard)}
                      {thin ? " · few" : ""}
                    </Box>
                  </Box>
                  <Box
                    aria-hidden
                    sx={{
                      mt: "3px",
                      height: 3,
                      borderRadius: "2px",
                      backgroundColor: alpha(chart.ink, 0.08),
                    }}
                  >
                    <Box
                      data-testid="pair-share"
                      sx={{
                        width: percent(jaccard),
                        height: "100%",
                        borderRadius: "2px",
                        backgroundColor: areaColourOf(regionIdOf(partner), chart),
                        opacity: thin ? 0.45 : 1,
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px", mt: 0.5 }}>
        <MuiLink component={Link} to={href} underline="hover" aria-label="Explore">
          Explore
        </MuiLink>
        <MuiLink
          component="button"
          type="button"
          underline="hover"
          onClick={() => onOpenEntity(node.capability.id, node.capability.type)}
          sx={LINK_BUTTON_SX}
        >
          Open capability
        </MuiLink>
      </Box>
    </Box>
  );
};

export default CapabilityMapPanel;
